import React, { useState, useCallback, useMemo } from 'react';
import { Box, Grid, Paper, useTheme, useMediaQuery } from '@mui/material';
import HourlyContent from './HourlyContent';
import useSelectedSensor from '../../hooks/useSelectedSensor';
import useSensorOptions from '../../hooks/useSensorOptions';
import useCompanyOptions from '../../hooks/useCompanyOptions';
import useSelectedCompany from '../../hooks/useSelectedCompany';
import { useHourlyDataQuery, useAdultnChildHourlyDataQuery, useGenderHourlyDataQuery, useGetAddonByDeviceQuery } from '../../services/api';
import dayjs from 'dayjs';
import HourlyHeader from './HourlyHeader';
import HourlyFilterBox from './HourlyFilterBox';
import HourlyLabel from './HourlyLabel';
import { useSelector } from 'react-redux';
import useGroupOptions from '../../hooks/useGroupOptions';
import useSelectedGroup from '../../hooks/useSelectedGroup';

const Hourly = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { company, role } = useSelector((state) => state.global);
    const isSuperOrPowerAdmin = role === 'Super Admin' || role === 'Power Admin';

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [showLabels, setShowLabels] = useState(true);
    const [tabValue, setTabValue] = useState(0);

    // Company, Device and group options
    const { companyOptions } = useCompanyOptions();
    const { selectedCompany, selectedCompanyId, handleCompanyChange } = useSelectedCompany({ companyOptions });
    const groupOptions = useGroupOptions(isSuperOrPowerAdmin ? selectedCompanyId : company);
    const sensorOptions = useSensorOptions(isSuperOrPowerAdmin ? selectedCompanyId : company);
    const { selectedGroup, handleGroupChange } = useSelectedGroup(groupOptions, isSuperOrPowerAdmin ? selectedCompanyId : company);
    const filteredSensorOptions = useMemo(() => {
        if (!groupOptions.length) {
            return sensorOptions;
        }

        const selectedGroupObj = groupOptions.find((group) => group._id === selectedGroup);
        const selectedDeviceIds = selectedGroupObj?.deviceIds || [];

        return sensorOptions.filter((sensor) => selectedDeviceIds.includes(sensor._id));
    }, [sensorOptions, groupOptions, selectedGroup]);
    const { selectedSensor, handleSensorChange, getSelectedSensorLabel } = useSelectedSensor(filteredSensorOptions, isSuperOrPowerAdmin ? selectedCompany : company);

    const startDate = selectedDate.clone().startOf('day').toISOString();
    const endDate = selectedDate.clone().endOf('day').toISOString();

    // API
    const { data: addonData } = useGetAddonByDeviceQuery(selectedSensor, { skip: !selectedSensor });
    const { data: hourlyData, isLoading: hourlyLoading, error: hourlyError } = useHourlyDataQuery({ mac: selectedSensor, startDate, endDate }, { skip: tabValue !== 0 || !selectedSensor });
    const {
        data: adultChildData,
        isLoading: adultChildLoading,
        error: adultChildError,
    } = useAdultnChildHourlyDataQuery({ mac: selectedSensor, startDate, endDate }, { skip: tabValue !== 1 || !selectedSensor });
    const { data: genderData, isLoading: genderLoading, error: genderError } = useGenderHourlyDataQuery({ mac: selectedSensor, startDate, endDate }, { skip: tabValue !== 2 || !selectedSensor });

    // Determine which data to use based on the selected tab
    const processedData = tabValue === 0 ? hourlyData : tabValue === 1 ? adultChildData : tabValue === 2 ? genderData : null;
    const chartIsLoading = tabValue === 0 ? hourlyLoading : tabValue === 1 ? adultChildLoading : tabValue === 2 ? genderLoading : null;
    const error = tabValue === 0 ? hourlyError : tabValue === 1 ? adultChildError : tabValue === 2 ? genderError : null;

    const handlePopoverOpen = useCallback((event) => setAnchorEl(event.currentTarget), []);
    const handlePopoverClose = useCallback(() => setAnchorEl(null), []);
    const handleDateChange = useCallback(
        (date) => {
            setSelectedDate(date);
            handlePopoverClose();
        },
        [handlePopoverClose]
    );

    const handleShowLabelsChange = (event) => {
        setShowLabels(event.target.checked);
    };

    const handleTabChange = (newValue) => {
        setTabValue(newValue);
    };

    return (
        <Box m={isSmallScreen ? '0.5rem' : '1rem 2.5rem'}>
            {isSmallScreen ? (
                // Mobile view
                <Grid container spacing={2} paddingBottom={3}>
                    <Grid item xs={12}>
                        <HourlyHeader isSmallScreen={isSmallScreen} selectedDate={selectedDate} />
                        <HourlyLabel sensorLabel={getSelectedSensorLabel()} isSmallScreen={isSmallScreen} tabValue={tabValue} onTabChange={handleTabChange} addonData={addonData} />
                    </Grid>
                    <Grid item xs={12}>
                        <Paper
                            sx={{
                                position: 'sticky',
                                top: 20,
                                height: 'auto',
                                overflow: 'auto',
                                borderRadius: '15px',
                                boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <HourlyFilterBox
                                sensorLabel={getSelectedSensorLabel()}
                                isSmallScreen={isSmallScreen}
                                showLabels={showLabels}
                                onShowLabelsChange={handleShowLabelsChange}
                                sensorOptions={filteredSensorOptions}
                                selectedSensor={selectedSensor}
                                handleSensorChange={handleSensorChange}
                                selectedDate={selectedDate}
                                handleDateChange={handleDateChange}
                                handlePopoverOpen={handlePopoverOpen}
                                handlePopoverClose={handlePopoverClose}
                                anchorEl={anchorEl}
                                companyOptions={companyOptions}
                                selectedCompany={isSuperOrPowerAdmin ? selectedCompany : null}
                                handleCompanyChange={isSuperOrPowerAdmin ? handleCompanyChange : null}
                                isSuperAdmin={isSuperOrPowerAdmin}
                                selectedGroup={selectedGroup}
                                handleGroupChange={handleGroupChange}
                                groupOptions={groupOptions}
                            />
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <HourlyContent
                            selectedSensor={selectedSensor}
                            showLabels={showLabels}
                            processedData={processedData}
                            chartIsLoading={chartIsLoading}
                            error={error}
                            isSmallScreen={isSmallScreen}
                            sensorLabel={getSelectedSensorLabel()}
                        />
                    </Grid>
                </Grid>
            ) : (
                // Web view
                <Grid container spacing={2} paddingBottom={3}>
                    <Grid item xs={12} md={10}>
                        <HourlyHeader isSmallScreen={isSmallScreen} selectedDate={selectedDate} />
                        <HourlyLabel sensorLabel={getSelectedSensorLabel()} isSmallScreen={isSmallScreen} tabValue={tabValue} onTabChange={handleTabChange} addonData={addonData} />
                        <HourlyContent
                            selectedSensor={selectedSensor}
                            showLabels={showLabels}
                            processedData={processedData}
                            chartIsLoading={chartIsLoading}
                            error={error}
                            isSmallScreen={isSmallScreen}
                            sensorLabel={getSelectedSensorLabel()}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Paper
                            sx={{
                                position: 'sticky',
                                top: 20,
                                height: 'auto',
                                overflow: 'auto',
                                borderRadius: '15px',
                                boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <HourlyFilterBox
                                sensorLabel={getSelectedSensorLabel()}
                                isSmallScreen={isSmallScreen}
                                showLabels={showLabels}
                                onShowLabelsChange={handleShowLabelsChange}
                                sensorOptions={filteredSensorOptions}
                                selectedSensor={selectedSensor}
                                handleSensorChange={handleSensorChange}
                                selectedDate={selectedDate}
                                handleDateChange={handleDateChange}
                                handlePopoverOpen={handlePopoverOpen}
                                handlePopoverClose={handlePopoverClose}
                                anchorEl={anchorEl}
                                companyOptions={companyOptions}
                                selectedCompany={isSuperOrPowerAdmin ? selectedCompany : null}
                                handleCompanyChange={isSuperOrPowerAdmin ? handleCompanyChange : null}
                                isSuperAdmin={isSuperOrPowerAdmin}
                                selectedGroup={selectedGroup}
                                handleGroupChange={handleGroupChange}
                                groupOptions={groupOptions}
                            />
                        </Paper>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default Hourly;
